import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ReplacementSuggestion } from '@replacements/types/replacementsDictionaryTypes';
import useLazyOxfordSuggestions from '@replacements/hooks/replacementsDictionary/useLazyOxfordSuggestions';
import fetchRespellingSuggestions from '@replacements/utils/fetchRespellingSuggestions';

export type RespellingRegion = 'us' | 'uk';
export type SuggestionsByRegion = Record<
  RespellingRegion,
  ReplacementSuggestion[]
>;

export enum RespellingRegionEnum {
  us = 'us',
  uk = 'uk',
}

export interface ReplacementsDictionaryContextProps {
  showDictionary: boolean;
  setShowDictionary: (shouldShow: boolean) => void;
  loading: boolean;
  wordInput?: string;
  setWordInput: (word: string | undefined) => void;
  suggestions: SuggestionsByRegion;
  replacement?: string;
  setSuggestions: (suggestions: SuggestionsByRegion) => void;
  handleSelect: (value: string) => void;
  reset: () => void;
}

const noop = () => {};

const getInitialState = (): ReplacementsDictionaryContextProps => ({
  showDictionary: false,
  setShowDictionary: noop,
  loading: false,
  suggestions: {
    us: [],
    uk: [],
  },
  setWordInput: noop,
  setSuggestions: noop,
  handleSelect: noop,
  reset: noop,
});

const ReplacementsDictionaryContext =
  createContext<ReplacementsDictionaryContextProps>(getInitialState());

interface ReplacementLibraryProviderProps {
  children: React.ReactNode;
}

// A "provider" is used to encapsulate only the
// components that needs the state in this context
export const ReplacementsDictionaryProvider = ({
  children,
}: ReplacementLibraryProviderProps) => {
  const [suggestions, setSuggestions] = useState<{
    us: Array<ReplacementSuggestion>;
    uk: Array<ReplacementSuggestion>;
  }>({
    us: [],
    uk: [],
  });
  const [showDictionary, setShowDictionary] = useState(false);
  const [wordInput, setWordInput] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [replacement, setReplacement] = useState<string>();

  const reset = useCallback(() => {
    if (showDictionary) {
      setShowDictionary(false);
      setReplacement(undefined);
      setSuggestions({ us: [], uk: [] });
    }
  }, [showDictionary]);

  /**
   * Upon hiding dictionary, reset replacement value
   */
  useEffect(() => {
    if (!showDictionary && replacement) {
      setReplacement(undefined);
      setSuggestions({ us: [], uk: [] });
    }
  }, [replacement, showDictionary]);

  const loadSuggestions = useLazyOxfordSuggestions(wordInput ?? '');

  const handleSelect = (value: string) => {
    setReplacement(value);
    setShowDictionary(false);
  };

  const handleSelectCallback = useCallback(handleSelect, []);

  const handleWordInput = useCallback(
    (value: string | undefined) => {
      const input = value?.trim();
      setWordInput(input);

      if (!input) return;

      let isMounted = true;

      const run = async () => {
        setLoading(true);
        const result = await fetchRespellingSuggestions({
          word: input,
          loadSuggestionsFn: loadSuggestions,
          handleSelect,
        });

        if (result && isMounted) {
          setLoading(false);
          setSuggestions(result);
        }
      };

      run();

      return () => {
        isMounted = false;
      };
    },
    [loadSuggestions, setLoading, setSuggestions]
  );

  const value = useMemo(
    () => ({
      wordInput,
      setWordInput: handleWordInput,
      showDictionary,
      setShowDictionary,
      loading,
      suggestions,
      replacement,
      setSuggestions,
      handleSelect: handleSelectCallback,
      reset,
    }),
    [
      wordInput,
      handleWordInput,
      showDictionary,
      loading,
      suggestions,
      replacement,
      handleSelectCallback,
      reset,
    ]
  );

  return (
    <ReplacementsDictionaryContext.Provider value={value}>
      {children}
    </ReplacementsDictionaryContext.Provider>
  );
};

export const useReplacementsDictionaryContext = () =>
  useContext(ReplacementsDictionaryContext);

export default ReplacementsDictionaryContext;
